/**
 * useConnectedClients — BLU-3xx Phase 47.01.2-B-fix MED-6
 *
 * Shared query wrapper around `locations.listConnectedClientsAtLocation`
 * with normalised return shape `{ id, name }[]`. Both `ClientFilterDropdown`
 * (read surface — narrows views) and `ClientTargetPicker` (write surface —
 * routes mutations) consume this — keeps the cache key consistent so React
 * Query dedupes the request when both render concurrently (e.g. opening
 * a write modal while the inventory page is mounted).
 *
 * Single source of truth for the staleTime (5min) — connected-client
 * rosters change at junction-claim cadence, not per-action.
 *
 * Behaviour:
 *  - Disabled when `locationId` is falsy (the schema requires a uuid;
 *    callers that don't have a location yet should pass `undefined`).
 *  - Returns `clients = []` while loading or in single-tenant fallback;
 *    callers can treat that case identically.
 *  - `enabled` defaults to `true`; pass `false` to suppress the fetch
 *    (e.g. when the SHARED_LOCATIONS_ENABLED flag is off).
 */
import { useMemo } from 'react';

import { trpc } from '@/lib/trpc';

export interface ConnectedRow {
  client_organization_id?: string;
  client_org?: { name?: string } | null;
}

export interface UseConnectedClientsArgs {
  locationId?: string;
  /** Default: true. Set to false to suppress the fetch when the
   *  caller already knows there are no clients (e.g. single-tenant). */
  enabled?: boolean;
}

export interface ConnectedClient {
  id: string;
  name: string;
}

/** Roster rows → `{ id, name }`, deduped by id (first row wins), sorted by name. */
export function toConnectedClients(rows: readonly ConnectedRow[]): ConnectedClient[] {
  const byId = new Map<string, ConnectedClient>();
  for (const row of rows) {
    const id = row.client_organization_id;
    const name = row.client_org?.name;
    if (typeof id === 'string' && typeof name === 'string' && !byId.has(id)) byId.set(id, { id, name });
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The active clients at SEVERAL floors (the union, deduped, by name): one
 * `listConnectedClientsAtLocation` read per floor, in parallel, sharing the
 * single-floor cache above. Used where a choice spans every floor the org owns
 * (Products' client catalogue switcher, Base garments' "Who can use it").
 *
 * Callers pass the floor ids already filtered to the ones the org OWNS; an
 * empty list makes no call. `clients` keeps its identity until a floor's data
 * changes, so it is safe in a memo or a header slot.
 */
export function useConnectedClientsAtLocations(locationIds: readonly string[]) {
  const queries = trpc.useQueries((tq) =>
    locationIds.map((locationId) =>
      tq.locations.listConnectedClientsAtLocation({ locationId }, { staleTime: 5 * 60_000 }),
    ),
  );
  const isLoading = queries.some((query) => query.isLoading);
  // `queries` is a new array every render; key the memo on each floor's data stamp.
  const dataKey = queries.map((query) => query.dataUpdatedAt).join(',');
  const idsKey = locationIds.join(',');
  const clients = useMemo(
    () => toConnectedClients(queries.flatMap((query) => (query.data ?? []) as ConnectedRow[])),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `dataKey` + `idsKey` track every floor's data.
    [idsKey, dataKey],
  );
  return { clients, isLoading };
}

export function useConnectedClients({ locationId, enabled = true }: UseConnectedClientsArgs) {
  const query = trpc.locations.listConnectedClientsAtLocation.useQuery(
    { locationId: locationId ?? '' },
    {
      enabled: enabled && !!locationId,
      staleTime: 5 * 60_000,
    },
  );

  const clients: ConnectedClient[] = useMemo(
    () => toConnectedClients((query.data ?? []) as ConnectedRow[]),
    [query.data],
  );

  return {
    clients,
    isLoading: query.isLoading && !!locationId,
    isError: query.isError,
    error: query.error,
  };
}
